import React, { useState, useEffect, useRef } from 'react';
import { Typography, Grid, Button, TextField, Icon, IconButton, Modal, Paper, LinearProgress, Collapse } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { get, set, size, last, sortBy } from 'global/lib/lodash';
import useStyles from './styles';
import ButtonToggle from 'global/components/btn-toggle';
import clsx from 'clsx';
import DropArea from 'global/components/drop-area';
import Image from 'material-ui-image';
import Camera from 'react-html5-camera-photo';
import 'react-html5-camera-photo/build/css/index.css';
import SnackAlerts from 'global/components/snack-alerts';
import useDebounce from 'global/hooks/useDebounce';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { CommentContainer } from 'feature/report/ChecklistScores';
import { ImageSelectBtn } from 'feature/report/AssessmentScore/SystemScore';
import { scoringClass } from '../../utils';
import { useScreen } from 'global/styles';
import { Button as OButton } from '../Button'

const CPLoading = (props) => {
  return (
    <Grid container spacing={2}>
      <Grid item xs={5}>
        <Skeleton width={'40%'} height={50} />
        <Skeleton variant="rect" height={250} />
      </Grid>
      <Grid item xs={7}>
        <Skeleton variant="rect" height={300} />
      </Grid>
    </Grid>
  );
};

export default (props) => {
  const { scoreOnly, isCourse } = props;
  const isActionable = get(props, 'checkpoint.filters.act', false);

  const [formState, _setFormState] = useState({});
  const [expandedExtraInfo, setExpandedExtraInfo] = useState(isActionable);
  const [showCamera, setShowCamera] = useState(false);
  // const [loadingImages, setLoadingImages] = useState(false);
  const [changedState, setChangedState] = useState(0);
  const [alerts, setAlerts] = useState();
  const currentRequest = useRef();
  const classes = useStyles({ formState });
  const debouncedForm = useDebounce(changedState, scoreOnly ? 0 : 500);
  const media = useRef({ deleted: [] });

  const check = get(props, 'checkpoint');
  const { isMobileXS } = useScreen();

  const isObs = !check.checkpointId;

  const scos = get(props, 'checkpointdata.data.scorecardOptions', [])
    .filter((f) => f.scorecardId == get(props, 'checkpoint.scorecardId') && f.checklistId == get(props, 'checkpoint.checklistId'))
    .sort((a, b) => a.value - b.value);
  const subTitle = get(props, 'checkpointdata.data.description');
  const scConfig = get(props, 'scorecard.config', {});

  const {
    // allowNA,
    allowNO,
    // defaultDisplay,
    // defaultScore,
    // defaultValue,
    // editScoreInfo,
    // hasSubScores,
    // maxScore,
    // minScore,
    // orderOverrides,
    // scoringClass,
    // step,
    // subScoringClass,
    weightOverrides,
  } = scConfig;

  // const isSubView = shouldUseSubEditView(scConfig);
  const canEditInfo = get(props, 'canEditInfo', true); // check.isSubScore ? !subScoringClass && !!editScoreInfo : true;
  const isSubScore = !!check.isSubScore;
  const isPassFail = isSubScore ? true : size(scos) === 2;

  let weight = 1;
  if (scoringClass(scConfig).isSummed) {
    weight = 0.01;
    if (weightOverrides) {
      const checklistWeight = weightOverrides.filter((w) => w.type === 'Checklist' && w.id === get(props, 'checklist.checklistId'))[0].value;
      if (checklistWeight) {
        weight = weight * checklistWeight;
      }
    }
  }

  const toggleCollapse = () => {
    setExpandedExtraInfo(!expandedExtraInfo);
  }

  useEffect(() => {
    setExpandedExtraInfo((isActionable || isObs || isCourse) && (isPassFail || isMobileXS));
  }, [isActionable, isMobileXS, isPassFail])

  const options = (() => {
    const key = isSubScore ? 'subScoreValueLabel' : 'scoreValueLabel';
    const ots = (isSubScore ? [{ value: 0 }, { value: 100 }] : scos)
      .sort((a, b) => get(a, 'value') - get(b, 'value'))
      .map((sco, i) => {
        const hasConfigLabel = (get(scConfig, key) || []).find(f => f.value == get(sco, 'value'));
        const label = hasConfigLabel ? get(hasConfigLabel, 'label') : (get(scConfig, 'displayPercentage') ? get(sco, 'value', '') + '%' : undefined);
        return {
          index: i,
          comment: [subTitle, get(sco, 'criteria')].filter(Boolean).join(' '),
          criteria: get(sco, 'criteria'),
          label: label,
          value: get(sco, 'value'),
          weight: sco.weight || weight,
        };
      });

    if (allowNO || isSubScore) {
      const hasConfigLabel = (get(scConfig, key) || []).find(f => f.value == -1);
      ots.push({
        comment: subTitle,
        label: get(hasConfigLabel, 'label', isPassFail ? (scoreOnly || isSubScore ? 'NEUTRAL' : 'N/O') : ''),
        value: -1,
        index: -1,
      });
    }

    return ots;
  })();

  const scores = useRetrieveData(
    { url: `/scorecard-scores/${get(check, 'scorecardScoreId')}` },
    {
      waitFor: get(check, 'scorecardScoreId') ? get(check, 'scorecardScoreId') : null,
      build: ['scorecardScores'],
    }
  );

  useEffect(() => {
    const scScoreIdIsNull = get(check, 'scorecardScoreId') === null;
    const isMinus1 = parseInt(get(check, 'value')) === -1;
    if (scScoreIdIsNull && isMinus1) {
      scores.manuallySetData({ ...check });
    }
  }, [check])

  const currentScore = last(scores.data);
  const scorecardScoreId = get(check, 'scorecardScoreId') || get(currentScore, 'id');

  const loading = get(props, 'loading', false) || (!scoreOnly && (check && check.scorecardScoreId) && get(scores, 'loading', false)) || (scoreOnly && !size(scos));

  const blocked = (props.queue || []).find((q) => q.id === check.checkpointId);

  const hideRightGrid = get(props, 'hideRightGrid', false);

  const setFormState = (state) => {
    _setFormState(state);
  };

  useEffect(() => {
    set(currentRequest, 'current.blocked', props.blocked);
  }, [props.blocked]);

  useEffect(() => {
    if (!blocked && !loading) {
      const scscores = get(scores, 'data', []);
      const currentScore = last(scscores);
      const checkScore = parseInt(get(check, 'value'));
      // Check for default values
      const ind = scoreOnly
        ? get(props, 'scoreOnlyIndex', 0)
        : isNaN(checkScore) && currentScore
          ? scos.findIndex((f) => parseInt(f.id) === get(currentScore, 'scorecardOptionId'))
          : scos.findIndex((f) => parseInt(f.value) === checkScore);
      const hasImages = get(check, 'images');
      const hasCriteria = get(currentScore, 'criteria');
      const hasNotes = isPassFail ? get(currentScore, 'notes') : get(check, 'notes');
      const keylength = Object.keys(formState).length;

      set(formState, 'scorecardOption', scoreOnly ? ind : checkScore === -1 ? -1 : isNaN(checkScore) ? null : ind);

      if (keylength === 0) {

        if (isObs) {
          set(formState, 'comment', get(currentScore, 'notes', check.notes));
        } else {
          //only do this on inital load
          if (hasNotes || hasCriteria) {
            set(formState, 'comment', hasCriteria);
            set(formState, 'overwritten', [subTitle, get(scos, `${ind}.criteria`)].filter(Boolean).join(' ') !== hasCriteria);
            set(formState, 'notes', hasNotes);
            setFormState(formState);
          }
        }
        if (hasImages) {
          // setLoadingImages(true);
          const imgs = hasImages.map((img, i) => {
            return {
              ...img,
              data: img.thumb,
            };
          });
          set(formState, 'photos', sortBy(imgs, 'orderColumn'));
          // setShowPhoto(true);
          // setLoadingImages(false);
        }
      }
      _setFormState({ ...formState });

      if (scoreOnly) {
        submit();
      }
    }
  }, [loading]);

  useEffect(() => {
    (check.images || []).forEach((img) => {
      const i = get(formState, 'photos', []).findIndex((p) => {
        if (p.id === img.id || !!p.mediaId) {
          return true;
        } else if (get(p, 'file._file')) {
          const name = get(p, 'file._file.attributes.name', get(p, 'file._file.attributes.fileName', '')).toLowerCase();
          const filename = get(p, 'file.name', '').toLowerCase();
          if (name.indexOf(filename) > -1) {
            return true;
          }
        }
      });
      const photoLoc = ['photos', i];
      const photo = get(formState, photoLoc);
      if (photo) {
        set(formState, photoLoc, {
          ...photo,
          id: img.id,
          data: img.thumb,
          file: undefined,
          mediaId: undefined,
        });
      }
    });
  }, [check.images]);

  // const onError = err => {
  //   setAlerts({
  //     message: err.message,
  //     severity: 'error',
  //   })
  // }

  const buildData = () => {
    const scorecardOption = get(formState, 'scorecardOption');
    const comment = get(formState, 'comment', '');
    const opt = get(options, scorecardOption);

    // const scscores = get(props, 'checkpoint.data.scorecardScores', []).filter(f => (get(f,'assessmentId','') && get(f, 'assessmentId', '').toString() === get(props, 'assessmentId', '').toString()));
    const cpoid = get(opt, 'checkpointOptionId');

    const data = {
      ids: {
        checkpoint: get(props, 'checkpoint._id'),
        checklist: get(props, 'checklist._id'),
      },
      deletedMedia: get(media, 'current.deleted', []),
      data: isObs ? {
        type: 'scorecard-scores',
        attributes: {
          superset_id: get(props, 'checklist.supersetId', '').toString(),
          assessment_id: get(props, 'assessmentId', '').toString(),
          scorecard_id: get(props, 'checklist.scorecardId', '').toString(),
          checklist_id: get(props, 'checklist.checklistId', '').toString(),
          checkpoint_id: null,
          checkpoint_option_id: null,
          scorecared_option_id: null,
          score: '-1',
          notes: get(formState, 'comment'),
          images: get(formState, 'photos', []).map((p) => ({ ...p, id: p.id || null, name: p.path, caption: get(p, 'caption', '') })),
        },
      } : {
        type: 'scorecard-scores',
        attributes: {
          superset_id: get(props, 'checkpoint.supersetId', '').toString(),
          assessment_id: get(props, 'assessmentId', '').toString(),
          scorecard_id: get(props, 'checkpoint.scorecardId', '').toString(),
          checklist_id: get(props, 'checkpoint.checklistId', '').toString(),
          checkpoint_id: get(props, 'checkpoint.checkpointId', '').toString(),
          checkpoint_option_id: cpoid ? cpoid.toString() : undefined,
          scorecard_option_id: opt ? get(opt, 'id', '').toString() : undefined,
          score: opt ? get(opt, 'value', '').toString() : scorecardOption,
          notes: get(formState, 'notes'),
          images: get(formState, 'photos', []).map((p) => ({ ...p, id: p.id || null, name: p.path, caption: get(p, 'caption', '') })),
        },
      },
    };

    if (!isObs) {
      set(data, 'data.attributes.notes', get(formState, 'notes', ''));
      set(data, 'data.attributes.criteria', comment || '');
    }

    if (scorecardScoreId) {
      set(data, 'data.id', scorecardScoreId.toString());
    }

    if (get(data, 'data.attributes.score') === null) {
      set(data, 'data.attributes', { archived: true, criteria: !isPassFail ? '' : undefined });
    }

    set(media, 'current.deleted', []);
    return data;
  };

  const stopProp = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const submit = () => {
    setChangedState(changedState + 1);
  };

  useEffect(() => {
    if (changedState === 0 && !scoreOnly) {
      return;
    }
    props.addToQueue &&
      props.addToQueue({
        id: check.checkpointId,
        build: buildData,
      });
  }, [debouncedForm]);

  const renderAddButtons = () => {
    if (!canEditInfo) {
      return null;
    }
    const buttonsEnabled = isObs ? !!get(formState, 'comment') : get(formState, 'scorecardOption', null) !== null;

    const onTextChange = (e) => {
      const val = e.target.value;
      set(formState, 'notes', val);
      setFormState({ ...formState });
      submit();
    };

    const onFilesChange = (files) => {
      set(formState, 'photos', [...files]);
      setFormState({ ...formState });
      submit();
    };

    const deleteImage = (file, index) => {
      const delMedia = get(media, 'current.deleted', []);
      const photos = get(formState, 'photos', []);
      const toRemove = photos[index];
      set(media, 'current.deleted', [...delMedia, toRemove]);
      photos.splice(index, 1);
      set(formState, 'photos', photos);
      setFormState({ ...formState });
      submit();
    };

    // const takeAPicture = e => {
    //   stopProp(e);
    //   setShowCamera(true);
    // }

    const handleTakePhoto = (data) => {
      const photos = get(formState, 'photos', []);
      set(formState, 'photos', [
        ...photos,
        {
          path: `Capture_${new Date().toISOString()}`,
          data,
        },
      ]);
      setFormState({ ...formState });
      setShowCamera(false);
      submit();
    };

    const onImageSubmit = ({ images }) => {
      set(formState, 'photos', [...get(formState, 'photos', []), ...images]);
      setFormState({ ...formState });
      submit();
    };

    const scoInd = get(formState, 'scorecardOption');

    const onOverrideChange = (e) => {
      const val = e.target.value;
      set(formState, 'comment', val);
      set(formState, 'overwritten', val !== [subTitle, get(scos, `${scoInd}.criteria`)].filter(Boolean).join(' ') ? true : false);
      setFormState({ ...formState });
      submit();
    };

    if (showCamera) {
      return (
        <Modal open={true} onClose={() => setShowCamera(false)}>
          <Paper className={classes.cameraContainer}>
            <Camera onTakePhoto={handleTakePhoto} />
            <Button color="primary" variant="contained" className={classes.closeCamera} onClick={() => setShowCamera(false)}>
              <Icon>close</Icon>
            </Button>
          </Paper>
        </Modal>
      );
    }

    const showTextOverride = get(scConfig, 'textOverride', false) && isPassFail;

    return (
      <div className={isPassFail ? undefined : classes.commentBtnContainer}>
        {!!showTextOverride && <div style={{ marginBottom: 24 }}>
          <TextField
            className={classes.comment}
            onChange={onOverrideChange}
            helperText={"Text Override"}
            value={get(formState, 'comment', '')}
            multiline
            rows={6}
          />
        </div>}

        <div className={classes.commentBtn}>
          <DropArea
            accept={'image/*'}
            onFilesChange={onFilesChange}
            onSort={onFilesChange}
            files={[...get(formState, 'photos', [])]}
            sortable={true}
            disabled={!buttonsEnabled}
            renderContent={() => <>Drop/Click to Upload Files</>}
            renderFile={(file, key) => {
              const i = get(formState, `photos`, []).findIndex((f) => f.id == key);
              const loc = `photos.${i}.caption`;
              return (
                <div className={classes.imageWrap} onClick={stopProp}>
                  <Image style={{ overflow: 'hidden' }} imageStyle={{ width: 'auto', margin: 'auto', right: 0, bottom: 0 }} src={file.data || file.src} />
                  {!file.file && (
                    <TextField
                      placeholder="caption"
                      value={get(formState, loc) || ''}
                      onChange={(e) => {
                        set(formState, loc, e.target.value);
                        setFormState({ ...formState });
                        submit();
                      }}
                      className={classes.comment}
                    />
                  )}
                  <IconButton className={classes.imageDelete} onClick={() => deleteImage(file, i)}>
                    <Icon>close</Icon>
                  </IconButton>
                </div>
              );
            }}
          />

          {!props.isCourse && (
            <div className={classes.imageSelectBtn}>
              <ImageSelectBtn disabled={!buttonsEnabled} onSubmit={onImageSubmit} superset_id={get(props, 'checklist.supersetId')} assessment={get(props, 'assessment.data')} />
            </div>
          )}
        </div>

        <div className={classes.commentBtn}>
          <TextField
            className={clsx(classes.comment, classes.commentWithClose)}
            onChange={onTextChange}
            helperText="Notes"
            placeholder="Additional notes"
            value={get(formState, 'notes', '')}
            multiline
            rows={5}
            disabled={!buttonsEnabled}
          />
        </div>
      </div>
    );
  };

  const renderLeftGrid = () => {
    const hasImage = get(props, 'checklist.checklist.image');

    const onSelectOption = (opt, index) => {
      const currentOption = get(formState, 'scorecardOption');
      const nullify = currentOption === index ? true : false;

      if (nullify && !scorecardScoreId) { return }

      set(formState, 'scorecardOption', nullify ? null : index);
      const currentComment = get(formState, 'comment');
      const overwritten = get(formState, 'overwritten');
      if (!overwritten || !currentComment) {
        opt.comment && set(formState, 'comment', opt.comment);
      }
      if (nullify) {
        set(formState, 'comment', '');
        set(formState, 'overwritten', false);
        // setShowPhoto(false);
      }
      setFormState({ ...formState });

      submit();
    };

    const comments = get(
      get(props, 'assessment.data.scorecardScores', []).find((c) => c.id == get(props, 'checkpoint.scorecardScoreId')),
      'comments',
      []
    );

    const renderText = () => {
      if (scoreOnly) {
        return null;
      }
      return (
        <>
          <Typography variant="h5">{get(props, 'checkpointdata.data.name')}</Typography>
          <Typography variant="subtitle1">{subTitle}</Typography>
          {!!size(comments) && comments.map((com) => <CommentContainer comment={com} isCheckpointEditMode />)}
        </>
      );
    };

    const btnDisabled = get(props, 'checkpoint.value') === null;

    return isObs ? null : (
      <Grid item xs={12} md={isPassFail || hideRightGrid ? (hasImage || hideRightGrid ? 12 : 7) : 5}>
        {renderText()}
        {props.renderObservations && props.renderObservations()}
        <ButtonToggle variant={isPassFail ? 'passFail' : undefined} options={options} selected={get(formState, 'scorecardOption')} onSelect={onSelectOption} scorecardConfig={scConfig} />
        {(isPassFail || isMobileXS) && !scoreOnly && canEditInfo && !hideRightGrid &&
          <>
            <OButton
              style={{ visibility: isCourse || isActionable || isObs ? 'hidden' : undefined }}
              className={classes.expandBtn}
              variant="default"
              endIcon={(<Icon>{expandedExtraInfo ? 'arrow_drop_up' : 'arrow_drop_down'}</Icon>)}
              disabled={btnDisabled}
              onClick={toggleCollapse}
              title={expandedExtraInfo ? 'Hide' : 'Show'}
            />
            {isPassFail &&
              <Collapse in={expandedExtraInfo}>
                {renderAddButtons()}
              </Collapse>
            }
          </>
        }
      </Grid>
    );
  };

  const renderRightGrid = () => {
    if (isPassFail || hideRightGrid || scoreOnly || !canEditInfo) {
      return null;
    }

    const scoInd = get(formState, 'scorecardOption');

    const onTextChange = (e) => {
      const val = e.target.value;
      set(formState, 'comment', val);
      set(formState, 'overwritten', val !== [subTitle, get(scos, `${scoInd}.criteria`)].filter(Boolean).join(' ') ? true : false);
      setFormState({ ...formState });
      submit();
    };

    const inputDisabled = isObs ? false : scoInd === null || scoInd === undefined;
    const textVisible = isObs ? true : get(scConfig, 'textOverride', false);

    return (
      <Grid item xs={12} md={isObs ? 12 : 7}>
        <div style={{ marginTop: isObs || isMobileXS ? 0 : 33 }}>
          {/* Always expanded when on large screen, but variable when on a small screen */}
          <Collapse in={!isMobileXS || expandedExtraInfo || isObs}>
            {!!textVisible && <TextField
              disabled={inputDisabled}
              className={isObs ? undefined : classes.comment}
              onChange={onTextChange}
              helperText={isObs ? undefined : "Text Override"}
              value={get(formState, 'comment', '')}
              multiline
              label={isObs ? 'General Observations' : undefined}
              placeholder={isObs ? 'General Observations' : undefined}
              rows={isObs ? 5 : 6}
              fullWidth={isObs}
              variant={isObs ? 'outlined' : undefined}
            />}
            {renderAddButtons()}
          </Collapse>
        </div>
      </Grid>
    );
  };

  if (get(props, 'loading', false)) return <CPLoading />;

  return (
    <Grid container spacing={2}>
      {renderLeftGrid()}

      {renderRightGrid()}
      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
      {blocked && <div className={classes.blockOverlay} />}
    </Grid>
  );
};
